import { useQuery } from '@tanstack/react-query';

import { ALL_MODULES_PAGE_SIZE } from '@/modules/players/v2/tabs/constants';
import { PlayerParticipationType } from '@/types/players.types';
import { TournamentStatus } from '@/types/playerTournaments.types';
import { getMyTournaments } from '@/utils/actions/players/non-edit/getMyTournaments';
import { getPlayerFavorites } from '@/utils/actions/players/non-edit/getPlayerFavorites';
import { getPlayerTournaments } from '@/utils/actions/players/non-edit/getPlayerTournaments';

const PLAYER_TOURNAMENTS_QUERY_KEY = 'player-tournaments';

interface UsePlayerTournametsArgs {
  playerId: string;
  playerPbUuid: string;
  tournamentStatus: TournamentStatus;
  activeTournamentType: PlayerParticipationType;
  searchParams: { [key: string]: string };
}

export const usePlayerTournaments = ({
  playerId,
  playerPbUuid,
  tournamentStatus,
  activeTournamentType,
  searchParams,
}: UsePlayerTournametsArgs) => {
  const statusType = tournamentStatus === TournamentStatus.Future ? 'upcoming' : 'past';
  const currentPage = Number(searchParams.page) || 1;

  const isManage = activeTournamentType === PlayerParticipationType.Managing;
  const isRegistered = activeTournamentType === PlayerParticipationType.RegisteredFor;

  return useQuery({
    queryKey: [
      PLAYER_TOURNAMENTS_QUERY_KEY,
      playerId,
      playerPbUuid,
      tournamentStatus,
      activeTournamentType,
      searchParams,
    ],

    queryFn: async () => {
      if (isRegistered) {
        const res = await getPlayerTournaments({
          id: playerId,
          type: statusType,
          pageSize: ALL_MODULES_PAGE_SIZE,
          page: currentPage,
          search: searchParams.search,
        });

        return {
          error: res.error,
          data: res.data?.tournaments ?? [],
          totalRecords: res.data?.totalRecords ?? 0,
        };
      }

      if (isManage) {
        const res = await getMyTournaments({
          myTournamentsActive: isManage,
          userId: playerPbUuid,
          type: 'm',
          date: tournamentStatus,
          search: searchParams.search,
        });

        const tournaments = res.data?.tournaments ?? [];

        return {
          error: res.error,
          data: tournaments,
          totalRecords: tournaments.length,
        };
      }

      const res = await getPlayerFavorites({
        id: playerId,
        type: statusType,
        pageSize: ALL_MODULES_PAGE_SIZE,
        page: currentPage,
        search: searchParams.search,
      });

      return {
        error: res.error,
        data: res.data?.tournaments ?? [],
        totalRecords: res.data?.totalRecords ?? 0,
      };
    },

    keepPreviousData: true,
  });
};
